import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Add as AddIcon } from '@mui/icons-material';
import {
    TextInput,
    Text,
    Button,
    Services,
    NumberInput,
    Modal,
    DateInput
} from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';
import PersonBlock from '../../components/PersonBlock.js';
import { addBeneficiary, editBeneficiary, removeBeneficiary } from '../../actions/users.js';
import * as notifier from '../../actions/notifier.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';
import BeneficiaryMessageCard from '../../components/MessageCards/BeneficiaryMessageCard.js';

class Beneficiaries extends PureComponent {
    state = {
        firstname: '',
        lastname: '',
        birthdate: this.props.birthdate ? Services.dates.formatIncomingDate(this.props.birthdate) : '',
        relationship: '',
        shares: '',
        type: '',
        beneficiaryID: null,
        dialogOpen: false,
        isEdit: false,
        primary: this.props.primary || [],
        contingent: this.props.contingent || [],
        hasSpouse: false,
        options: Services.selectFieldOptions.optionsWithoutSpouse(),
        deleteModalOpen: false,
        beneficiaryToRemoveID: false,
        visibility: false,
        showEffectiveDateDialog: false,
        effectiveDate: dates.getUTC(null, 'YYYY/MM/DD'),
        changeType: null,
        isContingent: false
    };

    componentDidMount() {
        // check if they have assigned a spouse beneficiary already
        this.props.primary.some((beneficiary) => {
            if (beneficiary.relationship === 'Spouse') {
                this.setState({
                    hasSpouse: beneficiary.beneficiaryID
                });

                return true;
            }

            return false;
        });

        this.props.contingent.some((beneficiary) => {
            if (beneficiary.relationship === 'Spouse') {
                this.setState({
                    hasSpouse: beneficiary.beneficiaryID
                });

                return true;
            }

            return false;
        });
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            primary: nextProps.primary,
            contingent: nextProps.contingent
        });
    }

    _close = () => {
        this.setState({
            firstname: '',
            lastname: '',
            relationship: '',
            shares: '',
            type: '',
            birthdate: '',
            beneficiaryID: null,
            firstnameError: null,
            lastnameError: null,
            relationshipError: null,
            typeError: null,
            sharesError: null,
            beneficiaryToRemoveID: false,
            dialogOpen: false,
            deleteModalOpen: false,
            changeType: null,
            showEffectiveDateDialog: false,
            effectiveDate: dates.getLocalDisplay(null, 'YYYY/MM/DD')
        });
    };

    _handleSelectChange = (type, event, id) => {
        if (event.target.value === 'Spouse') {
            this.setState({
                hasSpouse: id
            });
        }

        this._handleInputChange(type, event);
    };

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event) => {
        this.setState({
            [type]: event.target.value.toString()
        });
    };

    _handleSharesChange = (beneficiaryID, event) => {
        this.setState({
            primary: this.state.primary.map((beneficiary) => {
                if (beneficiary.beneficiaryID === beneficiaryID) {
                    return {
                        ...beneficiary,
                        shares: event.target.value
                    };
                }

                return beneficiary;
            }),
            contingent: this.state.contingent.map((beneficiary) => {
                if (beneficiary.beneficiaryID === beneficiaryID) {
                    return {
                        ...beneficiary,
                        shares: event.target.value
                    };
                }

                return beneficiary;
            })
        });
    };

    _checkShares = (checkContingent = false) => {
        let total = Number(this.state.shares);

        if (checkContingent) {
            this.state.contingent.map((beneficiary) => {
                if (beneficiary.beneficiaryID !== this.state.beneficiaryID) {
                    total = Number(total) + Number(beneficiary.shares);
                }

                return true;
            });
        } else {
            this.state.primary.map((beneficiary) => {
                if (beneficiary.beneficiaryID !== this.state.beneficiaryID) {
                    total = Number(total) + Number(beneficiary.shares);
                }

                return true;
            });
        }

        return Number(total) === 100;
    };

    /**
     * @param action
     * @returns {Promise<boolean>}
     * @private
     */
    _validateForm = async (action) => {
        const { firstname, lastname, shares, relationship, birthdate, type } = this.state;

        // ensure the shares percentage is valid before continuing
        let sharesValid = true;

        if (this.state.isContingent) {
            if (action === 'remove') {
                // if we are removing the only contingent we don't need to validate the shares
                if (this.props.contingent.length > 1) {
                    sharesValid = await this._checkShares(true);
                }
            } else {
                sharesValid = await this._checkShares(true);
            }
        } else if (action === 'remove') {
            // if we are removing the only primary we don't need to validate the shares
            if (this.props.primary.length > 1) {
                sharesValid = await this._checkShares(false);
            }
        } else {
            sharesValid = await this._checkShares(false);
        }

        if (!sharesValid) {
            this.setState({
                sharesError: `Shares must equal 100% across all ${ this.state.isContingent ? 'contingent' : 'primary' } beneficiaries.`
            });

            this.props.dispatch(notifier.displayError({ verbiage: `Shares must equal 100% across all ${ this.state.isContingent ? 'contingent' : 'primary' } beneficiaries.` }));
            return false;
        }

        if (action === 'remove') {
            return true;
        }

        const firstnameError = await Services.validateInput('firstname', firstname);
        const lastnameError = await Services.validateInput('lastname', lastname);
        const sharesError = await Services.validateInput('shares', shares);
        const typeError = await Services.validateInput('checkEmpty', type);
        let birthdateError = await Services.validateInput('birthdate', birthdate);
        const relationshipError = await Services.validateInput('relationship', relationship);

        // Check to see if the new Beneficiary is underage.
        const jsBirthdate = dates.getUTC(Services.dates.formatOutgoingDate(birthdate));
        const age = Math.floor((dates.getUTC() - jsBirthdate) / (60 * 60 * 24 * 365));

        // If the birthday is underage and there is no Trustee we must show an error
        if (age < 18 && !this.props.trustee) {
            birthdateError = 'If the beneficiary is under the age of 18 you must add a trustee first';
        }

        this.setState({
            firstnameError,
            lastnameError,
            sharesError,
            relationshipError,
            birthdateError,
            typeError
        });

        if (
            firstnameError ||
            lastnameError ||
            sharesError ||
            relationshipError ||
            birthdateError ||
            typeError
        ) {
            return false;
        }

        return true;
    };

    _validateEffectiveDate = async () => {
        const effectiveDateError = await Services.validateInput('effectiveDate', this.state.effectiveDate);

        await this.setState({
            effectiveDateError
        });

        if (effectiveDateError) {
            return false;
        }

        return true;
    };

    _openAddModal = (isContingent = false) => {
        let options = Services.selectFieldOptions.optionsWithoutSpouse();

        if (!this.state.hasSpouse) {
            options = Services.selectFieldOptions.optionsWithSpouse();
        }

        this.setState({
            firstname: '',
            lastname: '',
            relationship: '',
            type: '',
            shares: '',
            birthdate: '',
            isEdit: false,
            isContingent,
            options,
            firstnameError: false,
            lastnameError: false,
            sharesError: false,
            relationshipError: false,
            typeError: false,
            ageDisclaimer: false,
            beneficiaryToRemoveID: false,
            dialogOpen: true,
            changeType: 'save'
        });
    };

    _openEditModal = async (beneficiary, isContingent) => {
        let options = Services.selectFieldOptions.optionsWithoutSpouse();

        if (!this.state.hasSpouse || this.state.hasSpouse === beneficiary.beneficiaryID) {
            options = Services.selectFieldOptions.optionsWithSpouse();
        }

        await this.setState({
            beneficiaryID: beneficiary.beneficiaryID,
            firstname: beneficiary.firstname,
            lastname: beneficiary.lastname,
            relationship: beneficiary.relationship,
            type: beneficiary.type,
            shares: beneficiary.shares,
            birthdate: beneficiary.birthdate ? Services.dates.formatIncomingDate(beneficiary.birthdate) : '',
            firstnameError: false,
            lastnameError: false,
            sharesError: false,
            birthdateError: false,
            relationshipError: false,
            typeError: false,
            isEdit: true,
            totalShares: 0,
            dialogOpen: true,
            ageDisclaimer: false,
            options,
            isContingent,
            changeType: 'save'
        });
    };

    _remove = async () => {
        // loop through the beneficiaries in state and get only the ones we are not currently editing
        const beneficiaries = await this._getCurrentBeneficiaries();

        beneficiaries.contingent = this.state.isContingent ? beneficiaries.contingent : [];
        beneficiaries.primary = !this.state.isContingent ? beneficiaries.primary : [];

        const success = await this.props.dispatch(removeBeneficiary(
            this.props.user.userID,
            this.state.beneficiaryToRemoveID,
            beneficiaries,
            dates.getUTC(this.state.effectiveDate),
            this.state.isContingent
        ));

        if (success) {
            this._close();

            this.setState({
                beneficiaryToRemoveID: false,
                dialogOpen: false,
                deleteModalOpen: false,
                isContingent: false
            });

            return true;
        }

        return false;
    };

    _showDeleteDialog = (beneficiary, isContingent) => {
        this.setState({
            deleteModalOpen: true,
            shares: '',
            beneficiaryID: beneficiary.beneficiaryID,
            beneficiaryToRemoveID: beneficiary.beneficiaryID,
            isContingent,
            changeType: 'remove'
        });
    };

    _getCurrentBeneficiaries = () => {
        const beneficiaries = {
            primary: [],
            contingent: []
        };

        this.state.primary.map((beneficiary) => {
            if (beneficiary.beneficiaryID !== this.state.beneficiaryID) {
                beneficiaries.primary.push({
                    beneficiaryID: beneficiary.beneficiaryID,
                    firstname: beneficiary.firstname,
                    lastname: beneficiary.lastname,
                    relationship: beneficiary.relationship,
                    type: beneficiary.type,
                    birthdate: beneficiary.birthdate,
                    shares: Number(beneficiary.shares)
                });
            }

            return null;
        });

        this.state.contingent.map((beneficiary) => {
            if (beneficiary.beneficiaryID !== this.state.beneficiaryID) {
                beneficiaries.contingent.push({
                    beneficiaryID: beneficiary.beneficiaryID,
                    firstname: beneficiary.firstname,
                    lastname: beneficiary.lastname,
                    relationship: beneficiary.relationship,
                    type: beneficiary.type,
                    birthdate: beneficiary.birthdate,
                    shares: Number(beneficiary.shares)
                });
            }

            return null;
        });

        return beneficiaries;
    };

    /**
     * Called on save beneficiary
     * @private
     */
    _save = async () => {
        const isEffectiveDateValid = await this._validateEffectiveDate();

        if (!isEffectiveDateValid) {
            return false;
        }

        this._closeEffectiveDateDialog();

        // loop through the beneficiaries in state and get only the ones we are not currently editing
        const beneficiaries = await this._getCurrentBeneficiaries();

        const { firstname, lastname, shares, relationship, type, birthdate, beneficiaryID } = this.state;

        let success = false;
        const data = {
            firstname,
            lastname,
            relationship,
            shares,
            type,
            birthdate: Services.dates.formatOutgoingDate(birthdate),
            effectiveDate: dates.getUTC(this.state.effectiveDate)
        };

        if (this.state.isEdit) {
            success = await this.props.dispatch(editBeneficiary(this.props.user.userID, beneficiaryID, data, beneficiaries, this.state.isContingent));
        } else {
            success = await this.props.dispatch(addBeneficiary(this.props.user.userID, data, beneficiaries, this.state.isContingent));
        }

        if (success) {
            this.setState({
                isEdit: false,
                dialogOpen: false,
                isContingent: false,
                hasSpouse: relationship === 'Spouse'
            });

            return true;
        }

        // reset on error to the dialog form view
        this.setState({
            dialogOpen: true
        });

        return false;
    };

    _openEffectiveDateDialog = async (type) => {
        const isFormValid = await this._validateForm(type);

        if (!isFormValid) {
            return null;
        }

        // if the account is not yet setup then we don't need to ask for an effective date
        if (this.props.meta.accountEffDate === null) {
            if (type === 'remove') {
                return this._remove();
            }

            return this._save();
        }

        this.setState({
            dialogOpen: false,
            showEffectiveDateDialog: true
        });

        return null;
    };

    _closeEffectiveDateDialog = () => {
        this.setState({
            showEffectiveDateDialog: false,
            changeType: null
        });
    };

    render() {
        const primary = this.state.primary;
        const contingent = this.state.contingent;
        const styles = {
            currentBeneRow: {
                flexDirection: 'row'
            },
            titleBlock: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 10
            }
        };

        return (
            <div>
                <BeneficiaryMessageCard user={ this.props.user } />
                <div>
                    <div style={ styles.titleBlock }>
                        <Text mediumLarge>Primary Beneficiaries</Text>

                        <PermissionsComponent
                            permission="Beneficiaries"
                            accessType={this.props.accessType}
                            level="write"
                        >
                            <Button
                                onClick={ () => this._openAddModal(false) }
                                transparent
                                customStyle={{ marginLeft: 'auto' }}
                            >
                                <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                                    Add Beneficiary
                            </Button>
                        </PermissionsComponent>
                    </div>

                    {
                        primary.length === 0 ?
                            <Text testid="beneficiaries" light medium>You have not defined any primary beneficiaries.</Text>
                            :
                            primary.map((beneficiary, i) => (
                                <PersonBlock
                                    testid={i === 0 && "primary"}
                                    key={beneficiary.beneficiaryID}
                                    firstname={beneficiary.firstname}
                                    lastname={beneficiary.lastname}
                                    relationship={beneficiary.relationship}
                                    beneficiaryType={beneficiary.type}
                                    percentage={beneficiary.shares}
                                    dateAdded={beneficiary.dateAdded}
                                    parentID={beneficiary.parentID}
                                    status={beneficiary.type}
                                    type="beneficiary"
                                    edit={() => this._openEditModal(beneficiary, false)}
                                    remove={() => this._showDeleteDialog(beneficiary, false)}
                                />
                            ))
                    }
                </div>

                <div>
                    <div style={{ ...styles.titleBlock, marginTop: "30px" }}>
                        <Text mediumLarge>Contingent Beneficiaries</Text>

                        <PermissionsComponent
                            permission="Beneficiaries"
                            accessType={this.props.accessType}
                            level="write"
                        >
                            <Button
                                onClick={ () => this._openAddModal(true) }
                                transparent
                                customStyle={{ marginLeft: 'auto' }}
                            >
                                <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                                    Add Contingent Beneficiary
                            </Button>
                        </PermissionsComponent>
                    </div>

                    {
                        contingent.length === 0 ?
                            <Text testid="beneficiaries" light medium>You have not defined any contingent beneficiaries.</Text>
                            :
                            contingent.map((beneficiary, i) => (
                                <PersonBlock
                                    testid={i === 0 && "contingent"}
                                    key={beneficiary.beneficiaryID}
                                    firstname={beneficiary.firstname}
                                    lastname={beneficiary.lastname}
                                    relationship={beneficiary.relationship}
                                    beneficiaryType={beneficiary.type}
                                    percentage={beneficiary.shares}
                                    dateAdded={beneficiary.dateAdded}
                                    parentID={beneficiary.parentID}
                                    type="beneficiary"
                                    edit={() => this._openEditModal(beneficiary, true)}
                                    remove={() => this._showDeleteDialog(beneficiary, true)}
                                />
                            ))
                    }
                </div>

                <Modal
                    testid="add-edit-beneficiary-modal"
                    open={ this.state.dialogOpen }
                    onClose={this._close}
                    title={ this.state.isEdit ? 'Edit Beneficiary' : `${ this.state.isContingent ? 'Add Contingent Beneficiary' : 'Add Beneficiary' }` }
                    primaryButton={{
                        label: "Save Beneficiary",
                        onClick: () => this._openEffectiveDateDialog('save')
                    }}
                >
                    <React.Fragment>
                        <TextInput
                            label="First Name"
                            value={this.state.firstname}
                            onBlur={() => this._onBlur('firstname')}
                            onChange={(value) => this._handleInputChange('firstname', value)}
                            error={Boolean(this.state.firstnameError)}
                            helperText={this.state.firstnameError}
                            fullWidth
                        />

                        <TextInput
                            label="Last Name"
                            value={this.state.lastname}
                            onBlur={() => this._onBlur('lastname')}
                            onChange={(value) => this._handleInputChange('lastname', value)}
                            error={Boolean(this.state.lastnameError)}
                            helperText={this.state.lastnameError}
                            fullWidth
                        />

                        <TextInput
                            label="Relationship"
                            value={this.state.relationship}
                            fullWidth
                            onBlur={() => this._onBlur('relationship')}
                            onChange={(value) => this._handleSelectChange('relationship', value, this.state.beneficiaryID)}
                            error={Boolean(this.state.relationshipError)}
                            helperText={this.state.relationshipError}
                            select
                            selectOptions={this.state.options}
                        />

                        <DateInput
                            label="Date of Birth"
                            value={this.state.birthdate}
                            onBlur={() => this._onBlur('birthdate')}
                            onChange={(value) => this._handleInputChange('birthdate', value)}
                            error={Boolean(this.state.birthdateError)}
                            helperText={this.state.birthdateError}
                            fullWidth
                        />

                        <TextInput
                            label="Type"
                            value={this.state.type}
                            fullWidth
                            onBlur={() => this._onBlur('type')}
                            onChange={(value) => this._handleSelectChange('type', value, this.state.beneficiaryID)}
                            error={Boolean(this.state.typeError)}
                            helperText={this.state.typeError}
                            select
                            selectOptions={ this.state.isContingent ? [ 'Contingent' ] : [ 'Revocable', 'Irrevocable' ]}
                        />

                        <NumberInput
                            label="Percent of benefit"
                            suffix="%"
                            value={this.state.shares}
                            onBlur={() => this._onBlur('shares')}
                            onChange={(value) => this._handleInputChange('shares', value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    this._validateForm();
                                    e.preventDefault();
                                }
                            }}
                            error={Boolean(this.state.sharesError)}
                            helperText={this.state.sharesError}
                            fullWidth
                        />

                        <Text light small margin="20px 0 5px 0">
                            Please ensure your remaining { this.state.isContingent ? 'contingent' : 'primary' } beneficiaries shares collectively equal 100%.
                        </Text>

                        {
                            this.state.isContingent ?
                                contingent.map((beneficiary) => {
                                    if (beneficiary.beneficiaryID === this.state.beneficiaryID) {
                                        return null;
                                    }

                                    return (
                                        <div key={beneficiary.beneficiaryID} style={styles.currentBeneRow}>
                                            <NumberInput
                                                label={beneficiary.firstname}
                                                suffix="%"
                                                value={ beneficiary.shares }
                                                onChange={(event) => this._handleSharesChange(beneficiary.beneficiaryID, event)}
                                                error={Boolean(this.state.sharesError)}
                                                helperText={this.state.sharesError}
                                                fullWidth
                                            />
                                        </div>
                                    );
                                })
                                :
                                primary.map((beneficiary) => {
                                    if (beneficiary.beneficiaryID === this.state.beneficiaryID) {
                                        return null;
                                    }

                                    return (
                                        <div key={beneficiary.beneficiaryID} style={styles.currentBeneRow}>
                                            <NumberInput
                                                label={beneficiary.firstname}
                                                suffix="%"
                                                value={ beneficiary.shares }
                                                onChange={(event) => this._handleSharesChange(beneficiary.beneficiaryID, event)}
                                                error={Boolean(this.state.sharesError)}
                                                helperText={this.state.sharesError}
                                                fullWidth
                                            />
                                        </div>
                                    );
                                })
                        }
                    </React.Fragment>
                </Modal>

                <Modal
                    open={ this.state.deleteModalOpen }
                    onClose={this._close}
                    title={ 'Remove Beneficiary' }
                    primaryButton={{
                        label: "Remove",
                        onClick: () => this._openEffectiveDateDialog('remove')
                    }}
                    loader={this.props.loader}
                >
                    <Text small margin="10px 0 5px 0">
                        Please ensure your remaining { this.state.isContingent ? 'contingent' : 'primary' } beneficiaries shares collectively equal 100%.
                    </Text>

                    {
                        this.state.isContingent ?
                            contingent.map((beneficiary) => {
                                if (beneficiary.beneficiaryID === this.state.beneficiaryID) {
                                    return null;
                                }

                                return (
                                    <div key={beneficiary.beneficiaryID} styles={styles.currentBeneRow}>
                                        <NumberInput
                                            label={beneficiary.firstname}
                                            suffix="%"
                                            value={ beneficiary.shares }
                                            onChange={(event) => this._handleSharesChange(beneficiary.beneficiaryID, event)}
                                            error={Boolean(this.state.sharesError)}
                                            helperText={this.state.sharesError}
                                            fullWidth
                                        />
                                    </div>
                                );
                            })
                            :
                            primary.map((beneficiary) => {
                                if (beneficiary.beneficiaryID === this.state.beneficiaryID) {
                                    return null;
                                }

                                return (
                                    <div key={beneficiary.beneficiaryID} style={styles.currentBeneRow}>
                                        <NumberInput
                                            label={beneficiary.firstname}
                                            suffix="%"
                                            value={ beneficiary.shares }
                                            onChange={(event) => this._handleSharesChange(beneficiary.beneficiaryID, event)}
                                            error={Boolean(this.state.sharesError)}
                                            helperText={this.state.sharesError}
                                            fullWidth
                                        />
                                    </div>
                                );
                            })
                    }
                </Modal>

                <Modal
                    open={this.state.showEffectiveDateDialog}
                    transitionComponent={this._transition}
                    onClose={this._closeEffectiveDateDialog}
                    title="Set Effective Date"
                    primaryButton={{
                        label: "Submit",
                        onClick: this.state.changeType === 'remove' ? this._remove : this._save
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        Please provide the date you would like the change(s) to be effective:
                    </Text>

                    <DateInput
                        label="Effective Date"
                        value={this.state.effectiveDate}
                        onBlur={() => this._onBlur('effectiveDate')}
                        onChange={(e) => this._handleInputChange('effectiveDate', e)}
                        error={Boolean(this.state.effectiveDateError)}
                        helperText={this.state.effectiveDateError}
                        fullWidth
                        required
                    />

                    <Text medium margin="10px 0 0 0">
                        By clicking Submit below, the changes made will be applied and effective on the date provided above. Back dated changes will have adjustments applied using current rates.
                    </Text>
                </Modal>
                <span id="user-beneficiary" />
            </div>
        );
    }
}

Beneficiaries.propTypes = {
    primary: PropTypes.array,
    contingent: PropTypes.array,
    user: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    primary: state.users.currentUser.beneficiaries.primary,
    contingent: state.users.currentUser.beneficiaries.contingent,
    user: state.users.currentUser.meta,
    trustee: state.users.currentUser.trustee,
    accessType: state.master.access
});

export default (connect(mapStateToProps)(Beneficiaries));